// admin/src/services/websocketService.js - WebSocket 클라이언트 서비스
class WebSocketService {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 3000;
    this.listeners = new Map();
    this.heartbeatInterval = null;
  }

  /**
   * WebSocket 연결 시작
   * @param {string} url - WebSocket 서버 URL (선택사항, 환경 변수에서 자동 감지)
   */
  connect(url = null) {
    if (this.isConnected) {
      console.log('🔌 WebSocket이 이미 연결되어 있습니다.');
      return;
    }

    // URL이 제공되지 않으면 환경 변수에서 생성
    if (!url) {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
      
      // API Base URL이 없거나 localhost인 경우 WebSocket 연결 시도하지 않음
      if (!apiBaseUrl || apiBaseUrl.includes('localhost')) {
        console.log('⚠️ WebSocket 연결 건너뛰기: 로컬 개발 환경이거나 API Base URL이 설정되지 않음');
        return;
      }
      
      // HTTP/HTTPS URL을 WebSocket URL로 변환
      try {
        const apiUrl = new URL(apiBaseUrl);
        const wsProtocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
        url = `${wsProtocol}//${apiUrl.host}/ws`;
      } catch (error) {
        console.error('❌ WebSocket URL 생성 실패:', error);
        return;
      }
    }

    try {
      console.log('🔌 WebSocket 연결 시도:', url);
      this.ws = new WebSocket(url);

      this.ws.onopen = (event) => {
        console.log('✅ WebSocket 연결 성공');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        
        // 구독 요청
        this.subscribe(['remittances', 'dashboard']);
        
        // 하트비트 시작
        this.startHeartbeat();
        
        // 연결 이벤트 발생
        this.emit('connected', event);
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('🔌 WebSocket 메시지 수신:', data.type);
          
          // 특정 타입의 이벤트 발생
          this.emit(data.type, data);
          
          // 일반 메시지 이벤트도 발생
          this.emit('message', data);
        } catch (error) {
          console.error('🔌 WebSocket 메시지 파싱 에러:', error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('🔌 WebSocket 연결 종료:', event.code, event.reason);
        this.isConnected = false;
        this.stopHeartbeat();
        
        // 자동 재연결 시도
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          console.log(`🔌 WebSocket 재연결 시도 ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
          setTimeout(() => this.connect(url), this.reconnectInterval);
        } else {
          console.error('🔌 WebSocket 최대 재연결 시도 횟수 초과');
        }
        
        this.emit('disconnected', event);
      };

      this.ws.onerror = (error) => {
        console.error('🔌 WebSocket 에러:', error);
        this.emit('error', error);
      };

    } catch (error) {
      console.error('🔌 WebSocket 연결 실패:', error);
      this.emit('error', error);
    }
  }

  /**
   * WebSocket 연결 해제
   */
  disconnect() {
    if (this.ws) {
      this.stopHeartbeat();
      this.ws.close();
      this.ws = null;
      this.isConnected = false;
      console.log('🔌 WebSocket 연결 해제됨');
    }
  }

  /**
   * 채널 구독
   * @param {Array} channels - 구독할 채널 목록
   */
  subscribe(channels) {
    if (this.isConnected && this.ws) {
      this.ws.send(JSON.stringify({
        type: 'subscribe',
        channels: channels
      }));
      console.log('🔌 WebSocket 채널 구독:', channels);
    }
  }

  /**
   * 하트비트 시작
   */
  startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected && this.ws) {
        this.ws.send(JSON.stringify({
          type: 'ping',
          timestamp: new Date().toISOString()
        }));
      }
    }, 30000); // 30초마다 ping
  }

  /**
   * 하트비트 중지
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * 이벤트 리스너 등록
   * @param {string} event - 이벤트 타입
   * @param {Function} callback - 콜백 함수
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  /**
   * 이벤트 리스너 제거
   * @param {string} event - 이벤트 타입
   * @param {Function} callback - 콜백 함수
   */
  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * 이벤트 발생
   * @param {string} event - 이벤트 타입
   * @param {*} data - 이벤트 데이터
   */
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('🔌 WebSocket 이벤트 콜백 에러:', error);
        }
      });
    }
  }

  /**
   * 연결 상태 반환
   * @returns {boolean} 연결 상태
   */
  getConnectionStatus() {
    return this.isConnected;
  }
}

// 싱글톤 인스턴스
const webSocketService = new WebSocketService();

export default webSocketService;
